import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Select, Tabs, message, Tag, Tooltip, Switch } from 'antd';
import { ArrowLeftOutlined, WarningOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import {
  getStudent, updateStudent, getStudentNotes,
  getStudentParents, getStudentCharges, getStudentPayments,
  getStudentCertificates, getFollowups, setCertificateProgressVisibility,
} from '../api/students';
import { getStudentKvkkConsents } from '../api/kvkk';
import api from '../api/axios';
import StudentInfoTab from '../components/student/StudentInfoTab';
import StudentParentsTab from '../components/student/StudentParentsTab';
import StudentProgressTab from '../components/student/StudentProgressTab';
import StudentChargesTab from '../components/student/StudentChargesTab';
import StudentCompetitionsPanel from '../components/competitions/StudentCompetitionsPanel';
import StudentCertificatesTab from '../components/student/StudentCertificatesTab';
import StudentNotesTab from '../components/student/StudentNotesTab';
import StudentFollowupTab from '../components/student/StudentFollowupTab';
import StudentEnrollmentsTab from '../components/student/StudentEnrollmentsTab';
import StudentAttendanceTab from '../components/student/StudentAttendanceTab';
import StudentMakeupTab from '../components/student/StudentMakeupTab';
import StudentHistoryTab from '../components/student/StudentHistoryTab';
import StudentMediaTab from '../components/student/StudentMediaTab';
import StudentHistoricalCompletionTab from '../components/student/StudentHistoricalCompletionTab';
import useAuthStore from '../store/authStore';

const { Option } = Select;
const { TabPane } = Tabs;

const statusLabels = {
  candidate: { label: 'Aday', color: 'orange' },
  active: { label: 'Aktif', color: 'green' },
  passive: { label: 'Pasif', color: 'red' },
};

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'info';
  const { user: currentUser } = useAuthStore();
  const isSuperAdmin  = ['developer_admin', 'super_admin'].includes(currentUser?.role);
  const isManager     = ['developer_admin', 'super_admin', 'branch_manager'].includes(currentUser?.role);
  const isTeacher     = currentUser?.role === 'teacher';

  const [student, setStudent] = useState(null);
  const [notes, setNotes] = useState([]);
  const [parents, setParents] = useState([]);
  const [charges, setCharges] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [unapprovedConsents, setUnapprovedConsents] = useState([]);

  useEffect(() => {
    fetchAll();
    getStudentKvkkConsents(id)
      .then(res => setUnapprovedConsents((res.data.data || []).filter(c => c.status !== 'active')))
      .catch(() => {});
  }, [id]);

  const fetchAll = async () => {
    try {
      const [studentRes, notesRes, parentsRes, chargesRes, certRes, followupsRes] =
        await Promise.all([
          getStudent(id),
          getStudentNotes(id),
          getStudentParents(id),
          getStudentCharges(id).catch(() => ({ data: null })),
          getStudentCertificates(id),
          getFollowups(id),
        ]);

      setStudent(studentRes.data);
      setNotes(notesRes.data);
      setParents(parentsRes.data);
      setCharges(chargesRes.data);
      setCertificates(certRes.data);
      setFollowups(followupsRes.data);

      const enrollRes = await api.get(`/students/${id}/enrollments`);
      setEnrollments(enrollRes.data || []);
    } catch (error) {
      message.error('Öğrenci bilgileri yüklenemedi.');
    }
  };

  const handleStatusChange = async (value) => {
    try {
      await updateStudent(id, { status: value });
      message.success('Durum güncellendi.');
      fetchAll();
    } catch (error) {
      message.error('Durum güncellenemedi.');
    }
  };

  // Veli panosunda sertifika ilerleyişi (X/Y) görünsün mü.
  //
  // Bazı öğrenciler bilerek bir programda bekletiliyor (üst programa geçmek
  // için okuma-yazma/matematik yeterliliği oluşmamış ama çocuk gelmeyi
  // seviyor). Sayaç aylarca kıpırdamayınca veli "çocuğum neden hâlâ aynı
  // yerde" diye sorabiliyor.
  //
  // Kapatmak SADECE veli panosundaki ilerleyiş kartını kaldırır; kazanılmış
  // sertifikalar ve personel görünümü etkilenmez. Asıl kesme sunucuda
  // (ParentPortalController) — buradaki anahtar yalnızca onu yönetiyor.
  const handleProgressVisibility = async (checked) => {
    try {
      await setCertificateProgressVisibility(id, checked);
      message.success(checked
        ? 'Sertifika ilerleyişi veliye açıldı.'
        : 'Sertifika ilerleyişi veliden gizlendi.');
      fetchAll();
    } catch (error) {
      message.error('İşlem başarısız.');
    }
  };

  if (!student) return <div className="p-8 text-center">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/students')}>
            Geri
          </Button>
          <h2 className="text-2xl font-bold mb-0">
            {student.name} {student.surname}
          </h2>
          {unapprovedConsents.length > 0 && (
            <Tooltip
              title={unapprovedConsents
                .map(c => `${c.label} — ${c.status === 'rejected' ? 'reddedildi' : 'onaylanmadı'}`)
                .join(', ')}
            >
              <Tag icon={<WarningOutlined />} color="red">
                Eksik Onay Var
              </Tag>
            </Tooltip>
          )}
          <Select
            value={student.status}
            size="small"
            style={{ width: 110 }}
            disabled={isTeacher}
            onChange={handleStatusChange}
          >
            <Option value="candidate">Aday</Option>
            <Option value="active">Aktif</Option>
            <Option value="passive">Pasif</Option>
          </Select>

          {/* Yönetici anahtarı yönetir; öğretmen yalnızca durumu GÖRÜR —
              velinin ilerleyişi görmediğini bilmesi lazım, yoksa veliye
              "uygulamadan takip edin" der ve karşı taraf boş ekran görür. */}
          {isManager ? (
            <Tooltip title="Kapalıyken veli panosunda sertifika ilerleyişi (X/Y) görünmez. Kazanılmış sertifikalar etkilenmez.">
              <span className="flex items-center gap-2 text-sm">
                <Switch
                  size="small"
                  checked={student.show_certificate_progress !== false}
                  onChange={handleProgressVisibility}
                />
                {/* Kapalıyken turuncu: yönetici sayfayı tararken olağan dışı
                    olan durumun göze çarpması lazım, gri bir anahtar
                    kaçırılıyor. */}
                {student.show_certificate_progress === false ? (
                  <span className="text-orange-500 font-medium">
                    <EyeInvisibleOutlined className="mr-1" />
                    Veli ilerleyişi görsün
                  </span>
                ) : (
                  <span className="text-gray-500">Veli ilerleyişi görsün</span>
                )}
              </span>
            </Tooltip>
          ) : (
            student.show_certificate_progress === false && (
              <Tooltip title="Bu öğrencinin velisi sertifika ilerleyişini görmüyor.">
                <Tag icon={<EyeInvisibleOutlined />} color="orange">
                  Veliye ilerleyiş kapalı
                </Tag>
              </Tooltip>
            )
          )}
        </div>
      </div>

      <Tabs defaultActiveKey={defaultTab} onChange={(key) => { if (key === 'charges') getStudentCharges(id).then(r => setCharges(r.data)).catch(() => {}); }}>
        {/* Herkes görebilir */}
        <TabPane tab="Genel Bilgi" key="info">
          <StudentInfoTab student={student} onUpdate={fetchAll} />
        </TabPane>

        <TabPane tab="Proje Takibi" key="progress">
          <StudentProgressTab studentId={id} enrollments={enrollments} />
        </TabPane>

        <TabPane tab="Sertifikalar" key="certificates">
          <StudentCertificatesTab studentId={id} certificates={certificates} onUpdate={fetchAll} readOnly={isTeacher} />
        </TabPane>

        <TabPane tab="Öğrenci Notları" key="notes">
          <StudentNotesTab studentId={id} notes={notes} onUpdate={fetchAll} />
        </TabPane>

        {/* Veliler: herkes görebilir, sadece yöneticiler ekleyebilir */}
        <TabPane tab="Veliler" key="parents">
          <StudentParentsTab studentId={id} parents={parents} onUpdate={fetchAll} branchId={student?.branch_id} />
        </TabPane>

        {(isManager || isTeacher) && (
          <TabPane tab="Borç & Ödeme" key="charges">
            <StudentChargesTab studentId={id} charges={charges} onUpdate={fetchAll} branchId={student?.branch_id} student={student} readOnly={isTeacher} />
          </TabPane>
        )}

        {/*
          YARIŞMALAR — SALT OKUNUR (kullanıcının kararı: "öğrenci detayda
          görebilsin, işlem yapamaz, sadece görüntüler").

          `onTahsilat` verilmediği için tahsilat düğmesi hiç çizilmiyor;
          tahsilat ayrı sayfada (Yarışma Ödemeleri). Aynı bileşen iki
          yüzeyi besliyor, tek kaynak.

          Sekme izne bağlanmadı: uç `competitions.payments` VEYA
          `competitions.view` istiyor ve ikisi de yoksa 403 dönüyor —
          bileşen o durumda yalnızca hata mesajı gösterir. Sekmeyi
          burada gizlemek yetki değil; asıl kapı sunucuda.
        */}
        {(isManager || isTeacher) && (
          <TabPane tab="Yarışmalar" key="competitions">
            <StudentCompetitionsPanel studentId={id} />
          </TabPane>
        )}

        {(isManager || isTeacher) && (
          <TabPane tab="Kayıtlar" key="enrollments">
            <StudentEnrollmentsTab studentId={id} enrollments={enrollments} onUpdate={fetchAll} branchId={student?.branch_id} readOnly={isTeacher} />
          </TabPane>
        )}

        {(isManager || isTeacher) && (
          <TabPane tab="Yoklama" key="attendance">
            <StudentAttendanceTab studentId={id} enrollments={enrollments} readOnly={isTeacher} />
          </TabPane>
        )}

        {(isManager || isTeacher) && (
          <TabPane tab="Telafi Hakları" key="makeup">
            <StudentMakeupTab studentId={id} />
          </TabPane>
        )}

        {(isManager || isTeacher) && (
          <TabPane tab="Takip" key="followup">
            <StudentFollowupTab studentId={id} followups={followups} onUpdate={fetchAll} />
          </TabPane>
        )}

        {(isManager || isTeacher) && (
          <TabPane tab="Medya" key="videos">
            <StudentMediaTab studentId={id} />
          </TabPane>
        )}

        {/* Yalnızca şube müdürü ve üstü: döküm ödeme/indirim gibi kalemlerle
            genişleyebilir ve arka uç zaten öğretmene 403 dönüyor. Önceden
            sekme öğretmene de görünüyor, tıklayınca hata veriyordu. */}
        {isManager && (
          <TabPane tab="Geçmiş" key="history">
            <StudentHistoryTab studentId={id} />
          </TabPane>
        )}

        {(isManager || isTeacher) && (
          <TabPane tab="Geçmiş Sertifika / Proje" key="historical-completion">
            <StudentHistoricalCompletionTab studentId={id} onUpdate={fetchAll} />
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default StudentDetail;